import React, { useMemo, useState } from "react";
import { Animated, Platform, Pressable, ScrollView, Text, TextInput, View } from "react-native";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import { useDispatch } from "react-redux";
import { SustainTeamScheduleEntry, updateSustainTeamSchedule, UpdateScheduleBody } from "../../../store/thunks/ticketingPortalThunk";
import { useToast } from "../../../components/LhToastContainer";
import { s } from "../SustainTeamManagementScreen.styles";
import { CalendarDay, FilterOption, TeamMember, toDateKey, isWorkingOnDate } from "./SustainTeamTypes";
import CalendarFilterDropdown from "./CalendarFilterDropdown";
import { useTheme } from "../../../theme";

interface Props {
  teamData: TeamMember[];
  calendarDays: CalendarDay[];
  calendarScheduleMap: Record<string, Record<string, boolean>>;
  calendarToggles: Record<string, Record<string, boolean>>;
  rawScheduleData: SustainTeamScheduleEntry[];
  localTimeZoneAbbr: string;
  isCurrentUserLead: boolean;
  currentUserEmail: string;
  currentUserGpid: string;
  savingMemberIds: Set<string>;
  savingAnimValue: Animated.Value;
  onToggleScheduleDay: (memberId: string, day: CalendarDay) => void;
  onUpdateMemberTime: (memberId: string, which: "start" | "end", value: string) => void;
  onRefreshScheduleData: () => Promise<void>;
  /** Options for the Team filter dropdown. */
  teamFilterOptions: FilterOption[];
  /** Options for the Manager filter dropdown (derived from non-null managerEmail values). */
  managerFilterOptions: FilterOption[];
  /** Currently-selected team filter values (teamId as strings). */
  selectedTeams: string[];
  /** Currently-selected manager filter values (email strings). */
  selectedManagers: string[];
  onTeamsChange: (next: string[]) => void;
  onManagersChange: (next: string[]) => void;
  /** Total members count after server-side filtering (from totalCount in the API response). */
  filteredMemberCount: number;
}

type DayCustomRange = {
  start: string;
  end: string;
  chipLabel: string;
  tone: "custom" | "default" | "off";
};

const formatChipTime = (value: string): string => {
  const m = /^(\d{2}):(\d{2})$/.exec(value);
  if (!m) return value;
  const h24 = Number(m[1]);
  const min = m[2];
  const ampm = h24 >= 12 ? "p" : "a";
  const h12 = h24 % 12 || 12;
  return `${h12}:${min}${ampm}`;
};

const buildCustomChipLabel = (start: string, end: string): string =>
  `${formatChipTime(start)}-${formatChipTime(end)}`;

const normalizeDayNameForApi = (dayName: string): string => {
  if (!dayName) return dayName;
  const lower = dayName.toLowerCase();
  return `${lower.charAt(0).toUpperCase()}${lower.slice(1, 3)}`;
};

const parseDateKeyLocal = (dateKey: string): Date => {
  const [y, m, d] = dateKey.split("-").map(Number);
  return new Date(y, (m ?? 1) - 1, d ?? 1);
};

const getWeekStartKeyFromDateKey = (dateKey: string): string => {
  const date = parseDateKeyLocal(dateKey);
  const weekStart = new Date(date);
  const dayOfWeek = weekStart.getDay();
  const diffToMonday = weekStart.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
  weekStart.setDate(diffToMonday);
  return toDateKey(weekStart);
};

const formatApiCustomRange = (value: string): string => {
  const m = /^(\d{2}:\d{2})-(\d{2}:\d{2})$/.exec(value.trim());
  if (!m) return value;
  return buildCustomChipLabel(m[1], m[2]);
};

/**
 * Extract custom hours from API schedule data if available
 * Returns the formatted label or null if not a custom hours entry
 */
const getCustomHoursFromApi = (
  memberId: string,
  dateKey: string,
  dayName: string,
  rawScheduleData: SustainTeamScheduleEntry[],
): string | null => {
  const entry = rawScheduleData.find((e) => e.memberId.toString() === memberId);
  if (!entry?.schedule) return null;

  const weekKey = getWeekStartKeyFromDateKey(dateKey);
  const apiDayName = normalizeDayNameForApi(dayName);

  const daySchedule = entry.schedule[weekKey]?.[apiDayName];
  if (daySchedule && typeof daySchedule.is_working === "string") {
    // It's a custom hours entry (e.g., "06:04-18:04")
    return formatApiCustomRange(daySchedule.is_working);
  }

  return null;
};

const CalendarTable: React.FC<Props> = ({
  teamData,
  calendarDays,
  calendarScheduleMap,
  calendarToggles,
  rawScheduleData,
  localTimeZoneAbbr,
  isCurrentUserLead,
  currentUserEmail,
  currentUserGpid,
  savingMemberIds,
  savingAnimValue,
  onToggleScheduleDay,
  onUpdateMemberTime,
  onRefreshScheduleData,
  teamFilterOptions,
  managerFilterOptions,
  selectedTeams,
  selectedManagers,
  onTeamsChange,
  onManagersChange,
  filteredMemberCount,
}) => {
  const dispatch = useDispatch();
  const { show } = useToast();
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const [expandedMemberId, setExpandedMemberId] = useState<string | null>(null);
  const [draftCustomTimes, setDraftCustomTimes] = useState<Record<string, Record<string, DayCustomRange>>>({});
  const [customTimes, setCustomTimes] = useState<Record<string, Record<string, DayCustomRange>>>({});
  const [revertedDefaults, setRevertedDefaults] = useState<Record<string, Set<string>>>({});
  const [expandError, setExpandError] = useState<string>("");

  const dayKeys = useMemo(
    () => calendarDays.map((day) => toDateKey(day.dateObj)),
    [calendarDays],
  );

  const toggleExpanded = (memberId: string) => {
    setExpandError("");
    setExpandedMemberId((prev) => {
      const next = prev === memberId ? null : memberId;
      if (next) {
        // Always open editor with blank fields for the selected member.
        setDraftCustomTimes((prevDrafts) => ({ ...prevDrafts, [next]: {} }));
      }
      return next;
    });
  };

  const updateDraftTime = (
    memberId: string,
    dateKey: string,
    which: "start" | "end",
    value: string,
  ) => {
    setDraftCustomTimes((prev) => ({
      ...prev,
      [memberId]: {
        ...(prev[memberId] ?? {}),
        [dateKey]: {
          ...(prev[memberId]?.[dateKey] ?? { start: "", end: "" }),
          [which]: value,
        },
      },
    }));
  };

  const saveExpandedCustomHours = async (memberId: string) => {
    const memberDrafts = draftCustomTimes[memberId] ?? {};
    let skipped = 0;
    const toSave: Record<string, DayCustomRange> = {};

    Object.entries(memberDrafts).forEach(([dateKey, range]) => {
      const start = range.start.trim();
      const end = range.end.trim();
      const hasStart = !!start;
      const hasEnd = !!end;
      if (hasStart && hasEnd) {
        toSave[dateKey] = {
          start,
          end,
          chipLabel: buildCustomChipLabel(start, end),
          tone: "custom",
        };
      } else if (hasStart || hasEnd) {
        skipped += 1;
      }
    });

    if (skipped > 0) {
      setExpandError(`Custom hours need both a start and end time. ${skipped} day(s) were skipped.`);
      return;
    }

    // Find the member to get their info
    const member = teamData.find((m) => m.id === memberId);
    if (!member) {
      setExpandError("Member not found");
      return;
    }

    // If there are custom hours to save, call the API
    if (Object.keys(toSave).length > 0) {
      try {
        // Start with existing schedule data from API
        const existingScheduleEntry = rawScheduleData.find((entry) => entry.memberId.toString() === memberId);
        const existingSchedule = existingScheduleEntry?.schedule ?? {};
        
        // Build scheduleJson by merging existing data with new custom hours
        const scheduleJson: Record<string, Record<string, { is_working: boolean | string }>> = JSON.parse(JSON.stringify(existingSchedule));
        
        Object.entries(toSave).forEach(([dateKey, range]) => {
          const weekKey = getWeekStartKeyFromDateKey(dateKey);
          const calDay = calendarDays.find((d) => toDateKey(d.dateObj) === dateKey);
          const dayName = normalizeDayNameForApi(calDay?.dayKey ?? "");
          if (!dayName) return;
          
          if (!scheduleJson[weekKey]) {
            scheduleJson[weekKey] = {};
          }
          // Store custom time range as "HH:MM-HH:MM" in the is_working field using consistent day name format
          scheduleJson[weekKey][dayName] = {
            is_working: `${range.start}-${range.end}`,
          };
        });

        const updateBody: UpdateScheduleBody = {
          performerEmail: member.email,
          performerGpid: currentUserGpid,
          shiftStartCst: member.startHour,
          shiftEndCst: member.endHour,
          scheduleJson,
        };

        await dispatch(updateSustainTeamSchedule(member.id, updateBody) as any);

        // Update local state on success
        setCustomTimes((prev) => ({
          ...prev,
          [memberId]: {
            ...(prev[memberId] ?? {}),
            ...toSave,
          },
        }));

        await onRefreshScheduleData();
        setExpandError("");
        setExpandedMemberId(null);
        show(`Custom hours saved for ${member.name}.`, "success");
      } catch (err: any) {
        setExpandError(`Failed to save custom hours: ${err?.message || "Unknown error"}`);
      }
    } else {
      // No custom hours to save, just close the editor
      setExpandError("");
      setExpandedMemberId(null);
    }
  };

  const clearDraftAndCollapse = (memberId: string) => {
    setDraftCustomTimes((prev) => ({ ...prev, [memberId]: {} }));
    setExpandError("");
    setExpandedMemberId(null);
  };

  const clearCustomLabel = (memberId: string, dateKey: string) => {
    const member = teamData.find((m) => m.id === memberId);
    if (!member) {
      return;
    }

    const weekKey = getWeekStartKeyFromDateKey(dateKey);
    const calDay = calendarDays.find((d) => toDateKey(d.dateObj) === dateKey);
    const dayName = normalizeDayNameForApi(calDay?.dayKey ?? "");
    if (!dayName) {
      return;
    }

    const existingScheduleEntry = rawScheduleData.find((entry) => entry.memberId.toString() === memberId);
    const existingSchedule = existingScheduleEntry?.schedule ?? {};
    const scheduleJson: Record<string, Record<string, { is_working: boolean | string }>> = JSON.parse(JSON.stringify(existingSchedule));

    if (!scheduleJson[weekKey]) {
      scheduleJson[weekKey] = {};
    }

    // Revert to the default window represented by checked state.
    scheduleJson[weekKey][dayName] = { is_working: true };

    const updateBody: UpdateScheduleBody = {
      performerEmail: member.email,
      performerGpid: currentUserGpid,
      shiftStartCst: member.startHour,
      shiftEndCst: member.endHour,
      scheduleJson,
    };

    dispatch(updateSustainTeamSchedule(member.id, updateBody) as any)
      .then(async () => {
        setCustomTimes((prev) => {
          const nextMember = { ...(prev[memberId] ?? {}) };
          delete nextMember[dateKey];
          return { ...prev, [memberId]: nextMember };
        });

        // Hide API-provided custom label immediately until next data refresh.
        setRevertedDefaults((prev) => {
          const existing = prev[memberId] ?? new Set<string>();
          const nextSet = new Set(existing);
          nextSet.add(dateKey);
          return { ...prev, [memberId]: nextSet };
        });

        await onRefreshScheduleData();
        show("Reverted to default hours.", "success");
      })
      .catch(() => {
        setExpandError("Failed to revert custom hours. Please try again.");
      });
  };

  return (
  <View style={[s.calendarCard, isDark && { backgroundColor: "#202020" }]}>
    {/* ── Filter bar: Team + Manager dropdowns ── */}
    <View style={s.calendarFilterBar}>
      <CalendarFilterDropdown
        defaultLabel="All Teams"
        options={teamFilterOptions}
        selected={selectedTeams}
        onChange={onTeamsChange}
      />
      <CalendarFilterDropdown
        defaultLabel="All Managers"
        options={managerFilterOptions}
        selected={selectedManagers}
        onChange={onManagersChange}
      />
      <View style={s.calendarFilterSpacer} />
      <Text style={s.calendarFilterCount}>{filteredMemberCount} members</Text>
    </View>

    <View style={[s.calendarTip, isDark && { backgroundColor: "#0d2a44", borderColor: "#1a3a5c" }]}>
      <MaterialIcons name="info-outline" size={14} color={isDark ? "#3D8BFF" : "#034895"} />
      <Text style={[s.calendarTipText, isDark && { color: "#B0B0B0" }]}>
        Start and end times are in 24-hour CST. Click any cell to toggle a working day. On-duty status combines today's checked day with the current time window. Weekends are shaded.
      </Text>
    </View>

    <View style={[s.calendarTableWrap, isDark && { backgroundColor: "#202020" }]}>
      <ScrollView style={s.calendarTableVerticalScroll} showsVerticalScrollIndicator>
        <ScrollView horizontal showsHorizontalScrollIndicator contentContainerStyle={s.calendarTableContent}>
          <View>
            {/* ── Header row ── */}
            <View style={[s.calendarHeaderRow, isDark && { backgroundColor: "#1a1a1a", borderBottomColor: "#404040" }, s.calendarStickyHeaderRow]}>
              <View style={[s.calendarCellMember, s.calendarHeaderCell, s.calendarStickyMemberHeader, isDark && { backgroundColor: "#1a1a1a" }]}>
                <Text style={[s.calendarHeaderText, isDark && { color: "#B0B0B0" }]}>MEMBER</Text>
              </View>
              <View style={[s.calendarCellTime, s.calendarHeaderCell, s.calendarStickyStartHeader, isDark && { backgroundColor: "#1a1a1a" }]}>
                <Text style={[s.calendarHeaderText, isDark && { color: "#B0B0B0" }]}>START TIME</Text>
              </View>
              <View style={[s.calendarCellTime, s.calendarHeaderCell, s.calendarStickyEndHeader, isDark && { backgroundColor: "#1a1a1a" }]}>
                <Text style={[s.calendarHeaderText, isDark && { color: "#B0B0B0" }]}>END TIME</Text>
              </View>
              {calendarDays.map((day) => (
                <View
                  key={toDateKey(day.dateObj)}
                  style={[
                    s.calendarDayHeader,
                    day.isWeekend && (isDark ? { backgroundColor: "#2B2200" } : s.calendarWeekend),
                    day.isToday && (isDark ? { backgroundColor: "#0d2a44" } : s.calendarToday),
                    day.weekStart && s.calendarWeekSep,
                    isDark && { borderLeftColor: "#363636" },
                  ]}
                >
                  <Text style={[s.calendarDayLabel, isDark && { color: "#B0B0B0" }, day.isToday && (isDark ? { color: "#3D8BFF" } : s.calendarTodayText)]}>{day.dayLabel}</Text>
                  <Text style={[s.calendarDayDate, isDark && { color: "#F2F2F2" }, day.isToday && (isDark ? { color: "#3D8BFF" } : s.calendarTodayText)]}>{day.month} {day.date}</Text>
                </View>
              ))}
            </View>

            {/* ── Data rows ── */}
            {teamData.map((member, rowIdx) => {
              const canEditThisRow = isCurrentUserLead || member.email.toLowerCase() === currentUserEmail;
              const isSavingRow    = savingMemberIds.has(member.id);
              const isExpanded = expandedMemberId === member.id;

              if (isSavingRow) {
                return (
                  <View key={member.id} style={[s.calendarRow, isDark && { borderBottomColor: "#363636" }, rowIdx % 2 === 0 ? (isDark ? { backgroundColor: "#202020" } : s.rowEven) : (isDark ? { backgroundColor: "#1a1a1a" } : s.rowOdd)]}>
                    {/* Member cell skeleton */}
                    <View style={[s.calendarCellMember, s.calendarStickyMemberCell, isDark && { backgroundColor: rowIdx % 2 === 0 ? "#202020" : "#1a1a1a" }, { flexDirection: "row", alignItems: "center", gap: 10 }]}>
                      <Animated.View style={{ opacity: savingAnimValue, width: 28, height: 28, borderRadius: 14, backgroundColor: isDark ? "#2B2B2B" : "#E4E9F2" }} />
                      <View style={{ gap: 5 }}>
                        <Animated.View style={{ opacity: savingAnimValue, width: 88, height: 10, borderRadius: 4, backgroundColor: isDark ? "#2B2B2B" : "#E4E9F2" }} />
                        <Animated.View style={{ opacity: savingAnimValue, width: 52, height: 8,  borderRadius: 4, backgroundColor: isDark ? "#2B2B2B" : "#E4E9F2" }} />
                      </View>
                    </View>
                    {/* Start CST skeleton */}
                    <View style={[s.calendarCellTime, s.calendarStickyStartCell, isDark && { backgroundColor: rowIdx % 2 === 0 ? "#202020" : "#1a1a1a" }, { alignItems: "center", justifyContent: "center" }]}>
                      <Animated.View style={{ opacity: savingAnimValue, width: 76, height: 28, borderRadius: 6, backgroundColor: isDark ? "#2B2B2B" : "#E4E9F2" }} />
                    </View>
                    {/* End CST skeleton */}
                    <View style={[s.calendarCellTime, s.calendarStickyEndCell, isDark && { backgroundColor: rowIdx % 2 === 0 ? "#202020" : "#1a1a1a" }, { alignItems: "center", justifyContent: "center" }]}>
                      <Animated.View style={{ opacity: savingAnimValue, width: 76, height: 28, borderRadius: 6, backgroundColor: isDark ? "#2B2B2B" : "#E4E9F2" }} />
                    </View>
                    {/* Day cell skeletons */}
                    {calendarDays.map((day) => (
                      <View
                        key={toDateKey(day.dateObj)}
                        style={[s.calendarDayCell, day.isWeekend && (isDark ? { backgroundColor: "#2B2200" } : s.calendarWeekend), day.isToday && (isDark ? { backgroundColor: "#0d2a44" } : s.calendarToday), day.weekStart && s.calendarWeekSep, isDark && { borderLeftColor: "#363636" }, { alignItems: "center", justifyContent: "center" }]}
                      >
                        <Animated.View style={{ opacity: savingAnimValue, width: 18, height: 18, borderRadius: 3, backgroundColor: isDark ? "#2B2B2B" : "#E4E9F2" }} />
                      </View>
                    ))}
                  </View>
                );
              }

              return (
                <React.Fragment key={member.id}>
                  <View style={[
                    s.calendarRow,
                    isDark && { borderBottomColor: "#363636" },
                    rowIdx % 2 === 0 ? (isDark ? { backgroundColor: "#202020" } : s.rowEven) : (isDark ? { backgroundColor: "#1a1a1a" } : s.rowOdd),
                    isExpanded && (isDark ? { backgroundColor: "#0d2044" } : s.calendarExpandedRow),
                  ]}>
                    {/* Member cell */}
                    <View style={[s.calendarCellMember, s.calendarStickyMemberCell, isDark && { backgroundColor: rowIdx % 2 === 0 ? "#202020" : "#1a1a1a" }, isExpanded && (isDark ? { backgroundColor: "#0d2044" } : s.calendarExpandedSticky)]}>
                      <View style={s.calendarMemberCellContent}>
                        <View style={[s.avatar, { backgroundColor: member.avatarColor }]}>
                          <Text style={s.avatarText}>{member.initials}</Text>
                        </View>
                        <View style={{ flex: 1 }}>
                          <Text style={[s.memberName, isDark && { color: "#F2F2F2" }]}>{member.name}</Text>
                          <Text style={[s.memberRoleSub, isDark && { color: "#6E6E6E" }]}>{member.role === "lead" ? "Lead" : "Member"}</Text>
                        </View>
                        {canEditThisRow && (
                          <Pressable
                            onPress={() => toggleExpanded(member.id)}
                            style={({ pressed }) => [s.calendarExpandToggle, isDark && { backgroundColor: "#2B2B2B", borderColor: "#404040" }, pressed && s.calendarExpandTogglePressed]}
                          >
                            <MaterialIcons
                              name={isExpanded ? "expand-less" : "edit"}
                              size={14}
                              color={isExpanded ? "#fff" : (isDark ? "#3D8BFF" : "#034895")}
                            />
                          </Pressable>
                        )}
                      </View>
                    </View>

                    {/* Start CST */}
                    <View style={[s.calendarCellTime, s.calendarStickyStartCell, isDark && { backgroundColor: rowIdx % 2 === 0 ? "#202020" : "#1a1a1a" }, isExpanded && (isDark ? { backgroundColor: "#0d2044" } : s.calendarExpandedSticky)]}>
                      {Platform.OS === "web" ? (
                        <input
                          type="time"
                          value={member.startHour}
                          onChange={(e) => canEditThisRow && onUpdateMemberTime(member.id, "start", e.currentTarget.value)}
                          disabled={!canEditThisRow}
                          style={{ ...(s.timeInputWeb as any), ...(isDark ? { backgroundColor: "#2B2B2B", borderColor: "#404040", color: "#F2F2F2", colorScheme: "dark" } : {}), ...(!canEditThisRow ? { opacity: 0.4, cursor: "not-allowed" } : {}) }}
                        />
                      ) : (
                        <TextInput
                          value={member.startHour}
                          onChangeText={(value) => canEditThisRow && onUpdateMemberTime(member.id, "start", value)}
                          editable={canEditThisRow}
                          style={[s.timeInput, isDark && { backgroundColor: "#2B2B2B", borderColor: "#404040", color: "#F2F2F2" }, !canEditThisRow && { opacity: 0.4 }]}
                          placeholder="HH:MM"
                          placeholderTextColor={isDark ? "#6E6E6E" : "#9a9a9a"}
                          maxLength={5}
                        />
                      )}
                    </View>

                    {/* End CST */}
                    <View style={[s.calendarCellTime, s.calendarStickyEndCell, isDark && { backgroundColor: rowIdx % 2 === 0 ? "#202020" : "#1a1a1a" }, isExpanded && (isDark ? { backgroundColor: "#0d2044" } : s.calendarExpandedSticky)]}>
                      {Platform.OS === "web" ? (
                        <input
                          type="time"
                          value={member.endHour}
                          onChange={(e) => canEditThisRow && onUpdateMemberTime(member.id, "end", e.currentTarget.value)}
                          disabled={!canEditThisRow}
                          style={{ ...(s.timeInputWeb as any), ...(isDark ? { backgroundColor: "#2B2B2B", borderColor: "#404040", color: "#F2F2F2", colorScheme: "dark" } : {}), ...(!canEditThisRow ? { opacity: 0.4, cursor: "not-allowed" } : {}) }}
                        />
                      ) : (
                        <TextInput
                          value={member.endHour}
                          onChangeText={(value) => canEditThisRow && onUpdateMemberTime(member.id, "end", value)}
                          editable={canEditThisRow}
                          style={[s.timeInput, isDark && { backgroundColor: "#2B2B2B", borderColor: "#404040", color: "#F2F2F2" }, !canEditThisRow && { opacity: 0.4 }]}
                          placeholder="HH:MM"
                          placeholderTextColor={isDark ? "#6E6E6E" : "#9a9a9a"}
                          maxLength={5}
                        />
                      )}
                    </View>

                    {/* Day cells */}
                    {calendarDays.map((day) => {
                      const emailKey  = member.email.toLowerCase();
                      const dateKey   = toDateKey(day.dateObj);
                      const apiChecked = rawScheduleData.length > 0
                        ? ((calendarScheduleMap[emailKey]?.[dateKey]) ?? false)
                        : isWorkingOnDate(member, day.dateObj, day.dayKey);
                      const checked =
                        calendarToggles[emailKey] !== undefined && dateKey in calendarToggles[emailKey]
                          ? calendarToggles[emailKey][dateKey]
                          : apiChecked;

                      const customForDay = customTimes[member.id]?.[dateKey];
                      // Check for custom hours from API
                      const apiCustomHours = getCustomHoursFromApi(member.id, dateKey, day.dayKey, rawScheduleData);
                      const wasReverted = revertedDefaults[member.id]?.has(dateKey) ?? false;
                      const displayCustomHours = wasReverted ? null : (customForDay?.chipLabel || apiCustomHours);
                      const effectiveChecked = wasReverted ? true : checked;

                      return (
                        <View
                          key={`${member.id}-${dateKey}`}
                          style={[
                            s.calendarDayCell,
                            day.isWeekend && (isDark ? { backgroundColor: "#2B2200" } : s.calendarWeekend),
                            day.isToday && (isDark ? { backgroundColor: "#0d2a44" } : s.calendarToday),
                            day.weekStart && s.calendarWeekSep,
                            isDark && { borderLeftColor: "#363636" },
                            !canEditThisRow && { opacity: 0.45 } as any,
                          ]}
                        >
                          {displayCustomHours ? (
                            <View style={[
                              s.customTimeLabelWrap,
                              isDark && { backgroundColor: "#2B1A00", borderColor: "#5A3A00" },
                            ]}>
                              <Text style={[s.customTimeLabel, isDark && { color: "#FCD34D" }]}>{displayCustomHours}</Text>
                              {canEditThisRow && (
                                <Pressable
                                  onPress={() => clearCustomLabel(member.id, dateKey)}
                                  style={[s.customTimeClearBtn, isDark && { backgroundColor: "#3D2400" }]}
                                >
                                  <MaterialIcons name="close" size={10} color={isDark ? "#FCD34D" : "#8B6508"} />
                                </Pressable>
                              )}
                            </View>
                          ) : (
                            <Pressable
                              onPress={canEditThisRow ? () => onToggleScheduleDay(member.id, day) : undefined}
                              style={({ pressed }) => [
                                s.calendarCheckboxTapArea,
                                pressed && canEditThisRow && (isDark ? { backgroundColor: "#1a3a5c" } : s.calendarDayCellPressed),
                              ]}
                            >
                              <MaterialIcons
                                name={effectiveChecked ? "check-box" : "check-box-outline-blank"}
                                size={18}
                                color={effectiveChecked ? (isDark ? "#3D8BFF" : "#034895") : (isDark ? "#404040" : "#B7C0CC")}
                              />
                            </Pressable>
                          )}
                        </View>
                      );
                    })}
                  </View>

                  {isExpanded && (
                    <View style={[s.calendarRow, isDark && { borderBottomColor: "#363636" }, s.calendarExpandedEditorRow]}>
                      <View style={[s.calendarExpandedSideCell, isDark && { backgroundColor: "#0d2044", borderRightColor: "#1a3a5c" }]}>
                        <View style={s.calendarExpandedSideInner}>
                          <View style={{ flex: 1 }}>
                            <View style={s.calendarExpandedTitleRow}>
                              <MaterialIcons name="schedule" size={14} color={isDark ? "#3D8BFF" : "#034895"} />
                              <Text style={[s.calendarExpandedTitle, isDark && { color: "#F2F2F2" }]}>Custom hours (CST)</Text>
                            </View>
                            <Text style={[s.calendarExpandedHint, isDark && { color: "#B0B0B0" }]}>
                              Enter start and end for any day to override the checkbox for that day.
                            </Text>
                            {expandError ? <Text style={s.calendarExpandedError}>{expandError}</Text> : null}
                          </View>
                          <View style={s.calendarExpandedButtons}>
                            <Pressable style={[s.expandedSecondaryBtn, isDark && { backgroundColor: "#2B2B2B", borderColor: "#404040" }]} onPress={() => clearDraftAndCollapse(member.id)}>
                              <Text style={[s.expandedSecondaryBtnText, isDark && { color: "#F2F2F2" }]}>Cancel</Text>
                            </Pressable>
                            <Pressable style={s.expandedPrimaryBtn} onPress={() => saveExpandedCustomHours(member.id)}>
                              <Text style={s.expandedPrimaryBtnText}>Save</Text>
                            </Pressable>
                          </View>
                        </View>
                      </View>

                      {calendarDays.map((day) => {
                        const dateKey = toDateKey(day.dateObj);
                        const draft = draftCustomTimes[member.id]?.[dateKey] ?? { start: "", end: "" };
                        const hasValues = !!draft.start || !!draft.end;

                        return (
                          <View
                            key={`${member.id}-expanded-${dateKey}`}
                            style={[
                              s.calendarExpandedDayCell,
                              isDark && { backgroundColor: "#0d2044", borderLeftColor: "#1a3a5c" },
                              day.isWeekend && (isDark ? { backgroundColor: "#2B1800" } : s.calendarExpandedWeekend),
                              day.isToday && (isDark ? { backgroundColor: "#0a1a3a" } : s.calendarExpandedToday),
                              day.weekStart && s.calendarWeekSep,
                            ]}
                          >
                            {Platform.OS === "web" ? (
                              <View style={[
                                s.calendarExpandedDayBox,
                                hasValues && (isDark ? { backgroundColor: "#2B1A00", borderRadius: 6, padding: 3 } : s.calendarExpandedDayBoxFilled),
                              ]}>
                                <input
                                  type="time"
                                  value={draft.start}
                                  onChange={(e) => updateDraftTime(member.id, dateKey, "start", e.currentTarget.value)}
                                  style={{ ...(s.calendarExpandedTimeInputWeb as any), ...(isDark ? { backgroundColor: "#2B2B2B", borderColor: "#404040", color: "#F2F2F2", colorScheme: "dark" } : {}) }}
                                />
                                <input
                                  type="time"
                                  value={draft.end}
                                  onChange={(e) => updateDraftTime(member.id, dateKey, "end", e.currentTarget.value)}
                                  style={{ ...(s.calendarExpandedTimeInputWeb as any), ...(isDark ? { backgroundColor: "#2B2B2B", borderColor: "#404040", color: "#F2F2F2", colorScheme: "dark" } : {}) }}
                                />
                              </View>
                            ) : (
                              <View style={[
                                s.calendarExpandedDayBox,
                                hasValues && (isDark ? { backgroundColor: "#2B1A00", borderRadius: 6, padding: 3 } : s.calendarExpandedDayBoxFilled),
                              ]}>
                                <TextInput
                                  value={draft.start}
                                  onChangeText={(v) => updateDraftTime(member.id, dateKey, "start", v)}
                                  style={[s.calendarExpandedTimeInput, isDark && { backgroundColor: "#2B2B2B", borderColor: "#404040", color: "#F2F2F2" }]}
                                  placeholder="Start"
                                  placeholderTextColor={isDark ? "#6E6E6E" : "#9a9a9a"}
                                  maxLength={5}
                                />
                                <TextInput
                                  value={draft.end}
                                  onChangeText={(v) => updateDraftTime(member.id, dateKey, "end", v)}
                                  style={[s.calendarExpandedTimeInput, isDark && { backgroundColor: "#2B2B2B", borderColor: "#404040", color: "#F2F2F2" }]}
                                  placeholder="End"
                                  placeholderTextColor={isDark ? "#6E6E6E" : "#9a9a9a"}
                                  maxLength={5}
                                />
                              </View>
                            )}
                          </View>
                        );
                      })}
                    </View>
                  )}
                </React.Fragment>
              );
            })}
          </View>
        </ScrollView>
      </ScrollView>
    </View>
  </View>
  );
};

export default CalendarTable;
